/* eslint-disable indent */
import { ReleaseNote } from '@src/main/modules/parameters/domain/release-notes/release-note.interface';
import ReleaseNoteForm from '@src/main/modules/parameters/ui/release-notes/components/ReleaseNoteForm';
import { getReleaseNoteSchema } from '@src/main/modules/parameters/ui/release-notes/components/releaseNoteSchema';
import { useCreateReleaseNote } from '@src/main/modules/parameters/ui/release-notes/hooks/create/useCreateReleaseNote';
import { useDeleteReleaseNote } from '@src/main/modules/parameters/ui/release-notes/hooks/delete/useDeleteReleaseNote';
import { useReleaseNotes } from '@src/main/modules/parameters/ui/release-notes/hooks/find/useReleaseNotes';
import { useUpdateReleaseNote } from '@src/main/modules/parameters/ui/release-notes/hooks/update/useUpdateReleaseNote';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTableSingle from '@src/main/shared/ui/components/table/AppTableSingle';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

interface TableRow extends ReleaseNote {
  id: string | number;
}

const ReleaseNotes: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: releaseNotesTable = [], isLoading: loadingReleaseNotes } = useReleaseNotes();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditReleaseNote, setNewEditReleaseNote] = useState<ReleaseNote>({
    releaseNoteId: undefined,
    description: '',
    version: '',
  });
  const [selectedReleaseNotes, setSelectedReleaseNotes] = useState<TableRow[]>([]);
  const [releaseNoteToDelete, setReleaseNoteToDelete] = useState<TableRow | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasEditPermissions = hasPermission(PERMISSIONS.PARAMETERS.RELEASE_NOTES.edit);

  const toolbar = [false, hasEditPermissions, selectedReleaseNotes.length > 0, true, false, false];

  const { mutateAsync: createReleaseNote } = useCreateReleaseNote();
  const { mutateAsync: updateReleaseNote } = useUpdateReleaseNote();
  const { mutateAsync: deleteReleaseNote } = useDeleteReleaseNote();

  const releaseNotesWithId: TableRow[] = releaseNotesTable.map((s) => ({
    id: s.releaseNoteId!,
    ...s,
  }));

  const handleShowCreateReleaseNote = () => {
    setNewEditReleaseNote({
      releaseNoteId: undefined,
      description: '',
      version: '',
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleEditReleaseNote = (releaseNote: TableRow) => {
    setNewEditReleaseNote({ ...releaseNote });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    const schema = getReleaseNoteSchema(t);

    try {
      await schema.parseAsync(newEditReleaseNote);

      if (newEditReleaseNote.releaseNoteId) {
        await updateReleaseNote({
          id: String(newEditReleaseNote.releaseNoteId),
          data: {
            description: newEditReleaseNote.description,
          },
        });
        showSuccessMessage(toast, t('parameters.releaseNotes.updatedSuccess'));
      } else {
        await createReleaseNote({
          version: newEditReleaseNote.version,
          description: newEditReleaseNote.description,
        });
        showSuccessMessage(toast, t('parameters.releaseNotes.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['releaseNote'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const key = err.path[0] as string;
            errors[key] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleDeleteReleaseNote = (releaseNote: TableRow) => {
    setReleaseNoteToDelete(releaseNote);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        await Promise.all(selectedReleaseNotes.map((s) => deleteReleaseNote(s.releaseNoteId!)));
        setSelectedReleaseNotes([]);
      } else if (releaseNoteToDelete) {
        await deleteReleaseNote(releaseNoteToDelete.releaseNoteId!);
        setSelectedReleaseNotes((prev) =>
          prev.filter((s) => s.releaseNoteId !== releaseNoteToDelete.releaseNoteId),
        );
      }

      await queryClient.invalidateQueries({ queryKey: ['releaseNote'] });
      showSuccessMessage(toast, t('messages.releaseNotes.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setReleaseNoteToDelete(null);
      setIsBulkDelete(false);
    }
  };

  const rejectDelete = () => {
    setReleaseNoteToDelete(null);
    setIsBulkDelete(false);
  };

  const deleteSelectedReleaseNotes = () => {
    if (selectedReleaseNotes.length === 0) return;
    setIsBulkDelete(true);
  };

  return (
    <ContentCard>
      <HeaderTitle title="Release Notes" />

      <TableHeader
        onCreate={handleShowCreateReleaseNote}
        onDelete={deleteSelectedReleaseNotes}
        onRefresh={async () => {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ['releaseNote'] });
          setIsRefreshing(false);
        }}
        visibility={toolbar}
      />

      <AppLoading isLoading={loadingReleaseNotes || isRefreshing}>
        <AppTableSingle
          headers={[
            { name: 'Versión', field: 'version', width: '150px' },
            { name: 'Descripción', field: 'description' },
          ]}
          data={releaseNotesWithId}
          onEdit={hasEditPermissions ? handleEditReleaseNote : undefined}
          onDelete={hasEditPermissions ? handleDeleteReleaseNote : undefined}
          defaultSortField="version"
          defaultSortOrder={-1}
        />

        <AppDialog
          visible={isModalVisible}
          title={
            newEditReleaseNote.releaseNoteId
              ? t('parameters.releaseNotes.updateReleaseNote', {
                  version: newEditReleaseNote.version,
                })
              : t('parameters.releaseNotes.createReleaseNote')
          }
          onClose={() => setIsModalVisible(false)}
          onConfirm={handleCreateEdit}
        >
          <ReleaseNoteForm
            values={newEditReleaseNote}
            handleChange={(field, value) =>
              setNewEditReleaseNote((prev) => ({ ...prev, [field]: value }))
            }
            errors={validationErrors}
          />
        </AppDialog>

        <ConfirmDeleteDialog
          confirmDelete={confirmDelete}
          rejectDelete={rejectDelete}
          visible={isBulkDelete || releaseNoteToDelete !== null}
          message={t('parameters.releaseNotes.confirmDelete', {
            version: releaseNoteToDelete?.version,
          })}
        />
      </AppLoading>
    </ContentCard>
  );
};

export default ReleaseNotes;
